import React, { useCallback, useMemo, useRef, useState } from "react";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "../DataTable";
import { PageChanger } from "../PageChanger";
import { SearchBar } from "../SearchBar";
import { useSpotUser } from "@/utils/spotUser";
import {
  Workflow,
  WorkflowService,
  WorkflowSettingsInput,
  InputOverride,
} from "@/generated";
import { useQuery } from "@tanstack/react-query";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { ChevronLeftIcon, Plus, SearchX } from "lucide-react";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { TruncatedTextWithTooltip } from "../TruncatedTextWithTooltip";
import { AnimatePresence, motion } from "framer-motion";
import { Input } from "@/components/shadcn/input";
import {
  WorkflowVersionContext,
  WorkflowVersionInput,
} from "../Form/WorkflowVersionField";
import BaseDialog from "../Modals/BaseDialog";
import { Separator } from "../shadcn/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../shadcn/select";
import { Alert, AlertDescription, AlertTitle } from "../shadcn/alert";
import { FFService } from "@/services/FeatureFlags/FeatureFlags";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { ToolConfigStep } from "./ToolConfigStep";
import { timeAgo } from "@/utils/generic";

const PAGE_SIZE = 8;

const columnHelper = createColumnHelper<Workflow>();

const columns: ColumnDef<Workflow, any>[] = [
  columnHelper.accessor("name", {
    cell: (info) => {
      return (
        <div className="flex gap-2 items-center">
          <NoxusIcon icon={info.row.original?.meta?.icon || "workflow"} />
          <TruncatedTextWithTooltip>{info.getValue()}</TruncatedTextWithTooltip>
        </div>
      );
    },
    header: "Name",
  }),
  columnHelper.accessor("author_name", {
    cell: (info) => (
      <p className="text-sm text-muted-foreground truncate">
        {info.getValue()}
      </p>
    ),
    header: "Created by",
  }),
  columnHelper.accessor("created_at", {
    cell: (info) => (
      <p className="text-sm text-muted-foreground truncate">
        {info.getValue() ? timeAgo(info.getValue()) : "-"}
      </p>
    ),
    header: "Created date",
  }),
  columnHelper.accessor("last_run_at", {
    cell: (info) => (
      <p className="text-sm text-muted-foreground truncate">
        {info.getValue() ? timeAgo(info.getValue()) : "Never"}
      </p>
    ),
    header: "Last used",
  }),
];

interface WorkflowPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWorkflow: (props: {
    workflow_id: string;
    config: Record<string, unknown>;
    inputs_descriptions?: Record<string, string>;
    input_overrides?: Record<string, InputOverride>;
    version_id?: string;
  }) => void;
  openWorkflows: {
    id: string;
    name: string;
  }[];
  editingTool?: WorkflowSettingsInput;
  itemConfig?: Record<string, NoxusFormEntryConfig>;
  agentVariables?: Array<{ name: string }>;
  externalValuesEnabled?: boolean;
}

export const AssistantWorkflowPicker = ({
  isOpen,
  onClose,
  onSelectWorkflow,
  openWorkflows,
  editingTool,
  itemConfig,
  agentVariables,
  externalValuesEnabled,
}: WorkflowPickerProps) => {
  const { spotGroup } = useSpotUser();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Offered while the feature is switched on for the agent — never on whether
  // any external value happens to be declared. Gating it on the list meant
  // removing the last declaration left an already-configured input showing a
  // blank mode select, because the persisted `variable` mode had no option to
  // match.
  const canUseExternalValues =
    !!FFService.getVar("AGENT_VARIABLES_ENABLED") && !!externalValuesEnabled;

  const filterWorkflowIds = useMemo(() => {
    return openWorkflows?.map((owf) => owf.id) || [];
  }, [openWorkflows]);

  /* Workflow info */
  const { data: workflowsData, isLoading: isLoadingWorkflows } = useQuery({
    queryKey: [DATA_KEYS.WORKFLOWS, currentPage, filterWorkflowIds, search],
    queryFn: () =>
      WorkflowService.getWorkflows({
        path: { group_id: spotGroup.id },
        query: {
          size: PAGE_SIZE,
          order_by: "last_run_at",
          sort: "desc",
          query: search || undefined,
          page: currentPage,
          ignore_ids:
            filterWorkflowIds && filterWorkflowIds.length > 0
              ? filterWorkflowIds.join(",")
              : undefined,
        },
      }),
  });

  const { data: editingWorkflowData } = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    {
      path: {
        group_id: spotGroup.id,
        workflow_id: editingTool?.workflow_id || "",
      },
    },
    { staleTime: Infinity, enabled: !!editingTool?.workflow_id }
  );

  const handleBack = () => {
    setSelectedWorkflowId(null);
    setSelectedVersionId(null);
  };

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    editingTool?.workflow_id || null
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    editingTool?.version_id || null
  );

  const [workflowName, workflowIcon] = useMemo(() => {
    if (!selectedWorkflowId) return [null, null];

    let workflow: Workflow | undefined;

    if (editingWorkflowData) {
      workflow = editingWorkflowData;
    } else {
      workflow = workflowsData?.data.items.find(
        (w) => w.id === selectedWorkflowId
      );
    }
    const workflowIcon = workflow?.meta?.icon || "workflow";

    return [workflow?.name, workflowIcon];
  }, [selectedWorkflowId, workflowsData, editingWorkflowData]);

  const toolConfigRef = useRef<Record<string, unknown>>({
    extra_instructions: editingTool?.extra_instructions ?? "",
    tool_display_message_start:
      editingTool?.tool_display_message_start ?? "Running a workflow",
    tool_display_message_end:
      editingTool?.tool_display_message_end ?? "Workflow run complete",
  });

  const handleToolConfigChange = useCallback(
    (values: Record<string, unknown>) => {
      toolConfigRef.current = values;
    },
    []
  );

  // Add state for input descriptions
  const [inputDescriptions, setInputDescriptions] = useState<
    Record<string, string>
  >(editingTool?.inputs_descriptions || {});

  // State for input overrides (fixed value / variable mode)
  const [inputOverrides, setInputOverrides] = useState<
    Record<string, InputOverride>
  >(editingTool?.input_overrides ?? {});

  // Add query to get workflow inputs
  const { data: workflowInputs } = useQuery({
    queryKey: [
      DATA_KEYS.WORKFLOWS_ID,
      "versions",
      selectedWorkflowId,
      selectedVersionId,
    ],
    queryFn: () =>
      WorkflowService.getWorkflowInputs({
        path: {
          group_id: spotGroup.id,
          workflow_id: selectedWorkflowId || "",
        },
        query: {
          version_id: selectedVersionId || undefined,
        },
      }),
    enabled: !!selectedWorkflowId,
  });

  // Validate workflow version when selected
  const { data: validationResult, isLoading: isValidating } = useQuery({
    queryKey: [
      DATA_KEYS.WORKFLOWS_ID,
      "valid",
      selectedWorkflowId,
      selectedVersionId,
    ],
    queryFn: () =>
      WorkflowService.getIsValidWorkflow({
        path: {
          group_id: spotGroup.id,
          workflow_id: selectedWorkflowId || "",
        },
        query: {
          version_id: selectedVersionId || undefined,
        },
      }),
    enabled: !!selectedWorkflowId && !!selectedVersionId,
  });

  const isWorkflowInvalid = validationResult?.data?.is_valid === false;

  const validationErrorMessages = useMemo(() => {
    if (!validationResult?.data?.exceptions) return [];
    const messages: string[] = [];
    for (const [, errors] of Object.entries(validationResult.data.exceptions)) {
      for (const err of errors) {
        const snippet = err.snippet as string | undefined;
        const detail = err.detail as string | undefined;
        messages.push(snippet || detail || "Unknown validation error");
      }
    }
    return messages;
  }, [validationResult]);

  const handleSave = () => {
    if (selectedWorkflowId) {
      // Only include overrides that have values set
      const activeOverrides: Record<string, InputOverride> = {};
      for (const [key, override] of Object.entries(inputOverrides)) {
        if (override.value) {
          activeOverrides[key] = override;
        }
      }

      onSelectWorkflow({
        workflow_id: selectedWorkflowId,
        config: toolConfigRef.current,
        inputs_descriptions: inputDescriptions,
        input_overrides:
          Object.keys(activeOverrides).length > 0 ? activeOverrides : undefined,
        version_id: selectedVersionId || undefined,
      });
      onClose();
    }
  };

  // Only a *pinned* mode is limited to text. Value, External value and System
  // value all flow through `resolve_flow_input_overrides`, which returns
  // `dict[str, str]`, so they cannot express a file. "AI decides" does not go
  // through overrides at all — the input stays in the tool schema and the
  // workflow tool resolves a file the model names against the conversation's
  // session files (`_inject_file_ids`), so it is valid for every type.
  //
  // Inputs with a fixed value are exempt — the flow provides those itself and
  // they are never shown here. Read from the same query the fields render from,
  // so it also covers a flow configured before this restriction existed.
  const hasUnsupportedInputs = useMemo(
    () =>
      (workflowInputs?.data ?? []).some(
        (input) =>
          input.fixed_value == null &&
          (input.type?.data_type ?? "str") !== "str" &&
          inputOverrides[input.label]?.mode != null
      ),
    [workflowInputs, inputOverrides]
  );

  const footerActions = selectedWorkflowId
    ? [
        ...(editingTool
          ? []
          : [
              {
                label: "Back",
                props: {
                  variant: "ghost",
                  onClick: handleBack,
                  leftIcon: <ChevronLeftIcon size={12} />,
                },
              },
            ]),
        {
          label: editingTool ? "Save" : "Add flow",
          props: {
            onClick: handleSave,
            colorScheme: "blue",
            disabled:
              !selectedWorkflowId ||
              !selectedVersionId ||
              isWorkflowInvalid ||
              isValidating ||
              hasUnsupportedInputs,
          },
        },
      ]
    : [
        {
          label: "Cancel",
          props: {
            variant: "ghost",
            onClick: onClose,
          },
        },
      ];

  const workflowInputsArray = useMemo(() => {
    return workflowInputs?.data?.filter((input) => !input.fixed_value); // Only show non-fixed inputs
  }, [workflowInputs]);

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      size="5xl"
      title={
        workflowName ? (
          <div className="flex items-center gap-2">
            <NoxusIcon icon={workflowIcon || undefined} />
            {workflowName}
          </div>
        ) : (
          "Add Flow"
        )
      }
      footerActions={footerActions}
    >
      <div className="-m-1 overflow-x-hidden p-1">
        <AnimatePresence mode="wait" initial={false}>
          {!selectedWorkflowId ? (
            <motion.div
              key="step1"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <div>
                <div className="mb-2">
                  <SearchBar
                    placeholder="Search flows"
                    setValue={setSearch}
                    value={search}
                    isDebounced
                    autoFocus
                  />
                </div>

                <div className="w-full">
                  <DataTable
                    isLoading={isLoadingWorkflows}
                    columns={columns}
                    data={workflowsData?.data.items || []}
                    rowOnClick={(id) => {
                      setSelectedWorkflowId(id);
                    }}
                    loadingRows={8}
                    noDataMessage="No more flows available"
                    emptyState={
                      search ? (
                        <NoxusEmptyBlock
                          title="No flows"
                          description={
                            <>No flows match &ldquo;{search}&rdquo;.</>
                          }
                          icon={SearchX}
                          size="sm"
                          button="Clear search"
                          buttonVariant="outline"
                          onClick={() => setSearch("")}
                        />
                      ) : undefined
                    }
                    noShadow
                  />

                  {!!workflowsData &&
                    !!workflowsData?.data.pages &&
                    workflowsData.data.pages > 1 && (
                      <footer className="w-full pt-6 flex justify-center">
                        <PageChanger
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          totalPages={workflowsData?.data.pages || 0}
                          maxWidth="3rem"
                          size="sm"
                        />
                      </footer>
                    )}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <div>
                <div className="mb-6">
                  <WorkflowVersionContext.Provider value={selectedWorkflowId}>
                    <WorkflowVersionInput
                      label="Version"
                      value={selectedVersionId || undefined}
                      onChange={(e) => setSelectedVersionId(e)}
                      invalidTitle="This workflow is invalid and cannot be used as a tool:"
                      invalidReasons={
                        isWorkflowInvalid ? validationErrorMessages : []
                      }
                      flowHref={`/flows/${selectedWorkflowId}?version=${selectedVersionId}`}
                    />
                  </WorkflowVersionContext.Provider>
                </div>
                {selectedVersionId && !isWorkflowInvalid && (
                  <div className="w-full flex flex-col gap-4 items-stretch">
                    {/* Input descriptions section */}
                    {workflowInputsArray && workflowInputsArray.length > 0 && (
                      <div className="w-full flex flex-col gap-6">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-base text-primary font-semibold leading-none">
                            Input Instructions
                          </span>
                          <span className="text-sm font-regular text-muted-foreground">
                            Choose what information should be passed to the
                            inputs of this flow.
                          </span>
                        </div>

                        {hasUnsupportedInputs && (
                          <NoxusAlert
                            variant="warning"
                            size="xs"
                            title="Some inputs have an unsupported mode"
                            description="A non-text input can only be left to AI decides, which lets the agent pass a file from the conversation. Set those inputs back to AI decides to save."
                          />
                        )}

                        <div className="flex flex-col gap-4">
                          {workflowInputsArray.map((input) => {
                            const inputKey = input.label;
                            const override = inputOverrides[inputKey];
                            const mode = override?.mode || "ai";
                            const isTextInput =
                              (input.type?.data_type ?? "str") === "str";

                            return (
                              <div
                                key={inputKey}
                                className="flex flex-col gap-2"
                              >
                                <span className="text-sm font-medium text-foreground">
                                  {input.label}
                                </span>

                                {/* Editor first, mode select after it, on
                                        one row so the two line up. */}
                                <div className="flex items-start gap-2">
                                  <div className="min-w-0 flex-1">
                                    {mode === "ai" && (
                                      <Input
                                        placeholder="Describe what this input is used for..."
                                        value={
                                          inputDescriptions[inputKey] || ""
                                        }
                                        className="bg-white"
                                        onChange={(e) =>
                                          setInputDescriptions((prev) => ({
                                            ...prev,
                                            [inputKey]: e.target.value,
                                          }))
                                        }
                                      />
                                    )}

                                    {mode === "fixed" && (
                                      <Input
                                        disabled={!isTextInput}
                                        placeholder="Enter a value..."
                                        value={override?.value || ""}
                                        className="bg-white"
                                        onChange={(e) =>
                                          setInputOverrides((prev) => ({
                                            ...prev,
                                            [inputKey]: {
                                              mode: "fixed",
                                              value: e.target.value,
                                            },
                                          }))
                                        }
                                      />
                                    )}

                                    {mode === "variable" && (
                                      <Select
                                        disabled={!isTextInput}
                                        value={override?.value || ""}
                                        onValueChange={(value) =>
                                          setInputOverrides((prev) => ({
                                            ...prev,
                                            [inputKey]: {
                                              mode: "variable",
                                              value,
                                            },
                                          }))
                                        }
                                      >
                                        <SelectTrigger className="w-full">
                                          <SelectValue placeholder="Select an external value..." />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-64 overflow-y-auto">
                                          {(agentVariables?.length ?? 0) ===
                                          0 ? (
                                            <p className="px-2 py-1.5 text-sm text-muted-foreground">
                                              No external values declared yet.
                                            </p>
                                          ) : (
                                            (agentVariables ?? []).map((v) => (
                                              <SelectItem
                                                key={v.name}
                                                value={v.name}
                                              >
                                                {v.name}
                                              </SelectItem>
                                            ))
                                          )}
                                        </SelectContent>
                                      </Select>
                                    )}

                                    {mode === "conversation_property" && (
                                      <Select
                                        disabled={!isTextInput}
                                        value={override?.value || ""}
                                        onValueChange={(value) =>
                                          setInputOverrides((prev) => ({
                                            ...prev,
                                            [inputKey]: {
                                              mode: "conversation_property",
                                              value,
                                            },
                                          }))
                                        }
                                      >
                                        <SelectTrigger className="w-full">
                                          <SelectValue placeholder="Select property..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="conversation_id">
                                            Conversation ID
                                          </SelectItem>
                                          <SelectItem value="assistant_id">
                                            Assistant ID
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    )}
                                  </div>

                                  <NoxusTooltip
                                    content={
                                      isTextInput
                                        ? ""
                                        : "Non-text inputs can only use an AI rule."
                                    }
                                    placement="top"
                                  >
                                    <Select
                                      value={mode}
                                      onValueChange={(value) => {
                                        if (value === "ai") {
                                          setInputOverrides((prev) => {
                                            const next = { ...prev };
                                            delete next[inputKey];
                                            return next;
                                          });
                                        } else {
                                          setInputOverrides((prev) => ({
                                            ...prev,
                                            [inputKey]: {
                                              mode: value as
                                                | "fixed"
                                                | "variable"
                                                | "conversation_property",
                                              // Cleared on a mode change: a
                                              // variable name is not a valid
                                              // literal, nor the reverse.
                                              value:
                                                value === mode
                                                  ? override?.value || ""
                                                  : "",
                                            },
                                          }));
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="w-[150px] shrink-0">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="ai">
                                          AI decides
                                        </SelectItem>
                                        {isTextInput && (
                                          <SelectItem value="fixed">
                                            Value
                                          </SelectItem>
                                        )}
                                        {isTextInput &&
                                          (canUseExternalValues ||
                                            mode === "variable") && (
                                            <SelectItem value="variable">
                                              External value
                                            </SelectItem>
                                          )}
                                        {isTextInput && (
                                          <SelectItem value="conversation_property">
                                            System value
                                          </SelectItem>
                                        )}
                                      </SelectContent>
                                    </Select>
                                  </NoxusTooltip>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {workflowInputsArray && workflowInputsArray.length > 0 && (
                      <Separator className="my-2" />
                    )}

                    {itemConfig && (
                      <ToolConfigStep
                        itemConfig={itemConfig}
                        defaultValues={toolConfigRef.current}
                        onChange={handleToolConfigChange}
                      />
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </BaseDialog>
  );
};
